/**
 * Rate card — every price in the system on one screen.
 *
 * Model rates, carrier rates, per-session/per-minute cards and credit prices
 * used to live on three separate admin pages with no view of how they compose.
 * A pricing decision is a decision about the GAP between two of these numbers,
 * and you cannot make it while looking at one of them.
 *
 * Order is cost → price, so the screen reads the way the decision does: this
 * is what a minute costs us, and this is what we charge for one.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  AdminAPI, CarrierRate, ChannelRateCard, MessagingRate, UnpricedMessaging,
  UsageRateCard,
} from '../../lib/api';
import {
  Card, Chip, Empty, Notice, SCOPE_LABEL, TRANSPORT_LABEL, Table, Td, Th,
  capitalise, rate, symbolFor, useCurrency,
} from './shared';

const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** Plain names for the per-action credit prices (28 Sep — the Rate card page's words). */
const ACTION_LABEL: Record<string, string> = {
  lead_loaded: 'Lead',
  outcome_qualified: 'Success: lead qualified',
  outcome_meeting_booked: 'Success: meeting booked',
  outcome_converted: 'Success: lead converted',
  ui_agent_interaction: 'Conversation',
  app_agent_interaction: 'App assistant session',
  call_connected: 'Answered call',
  call_minute: 'Call time',
  ui_agent_creation: 'Agent generated',
  user_interaction: 'Conversation (retired)',
  agent_turn: 'Agent turn (not charged)',
  test_agent_run: 'Test run',
  agent_create: 'Agent created',
  component_create: 'Component created',
  knowledge_base_create: 'Knowledge base created',
};

export const RateCardTab: React.FC<{ api: AdminAPI; nonce: number }> = ({ api, nonce }) => {
  const [card, setCard] = useState<UsageRateCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.getUsageRateCard()
      .then((c) => { setCard(c); setError(null); })
      .catch((e) => setError(`Couldn't load the rate card: ${errorText(e)}. Refresh to try again.`));
  }, [api]);

  useEffect(load, [load, nonce]);

  return (
    <div className="space-y-4">
      {error && <Notice tone="error">{error}</Notice>}
      {!card && !error && <Notice>Loading the rate card…</Notice>}

      {card && (
        <>
          <RetiredChannelCards cards={card.channel_rate_cards} />

          <Carriers
            rows={card.carriers}
            api={api}
            saving={saving}
            setSaving={setSaving}
            onChanged={load}
            onError={setError}
          />

          <MessagingRates
            rows={card.messaging}
            gaps={card.unpriced_messaging}
            api={api}
            saving={saving}
            setSaving={setSaving}
            onChanged={load}
            onError={setError}
          />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <Models rows={card.models} />
            <CreditPrices actions={card.credit_actions} settings={card.pricing_settings} />
          </div>
        </>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Retired channel rates (28 Sep, mig 0702)
// ---------------------------------------------------------------------------

/**
 * channel_rate_cards stopped being a price on 28 Sep. Every thing is priced by
 * ONE rate card (credit_pricing_defaults + per-organisation overrides, on the
 * Rate card page); per-minute calls are call_minute × started minutes. 0702
 * deactivated these rows and copied any organisation-scoped ones onto that
 * card. They stay listed, read-only, so older reports explain themselves; the
 * credits billable and the margin on these screens now come from the ledger —
 * what was actually charged.
 */
const RetiredChannelCards: React.FC<{ cards: ChannelRateCard[] }> = ({ cards }) => (
  <Card
    title="Retired channel rates (28 Sep)"
    subtitle="No longer charged. Prices are on the Rate card page; credits billable here come from the ledger."
    bodyClassName=""
  >
    <Table
      head={
        <tr>
          <Th>Scope</Th>
          <Th>Channel</Th>
          <Th align="right">Per session</Th>
          <Th align="right">Per minute</Th>
          <Th>Note</Th>
        </tr>
      }
    >
      {cards.map((c) => (
        <tr key={c.id} className="text-gray-500">
          <Td>
            {SCOPE_LABEL[c.scope] ?? capitalise(c.scope)}
            {c.organization_name && <div className="text-[11px] text-gray-400">{c.organization_name}</div>}
            {c.agent_ref && <div className="text-[11px] text-gray-400">{c.agent_ref}</div>}
          </Td>
          <Td><Chip kind="channel" value={c.channel} /></Td>
          <Td align="right" className="tabular-nums">{c.per_session_credits} cr</Td>
          <Td align="right" className="tabular-nums">{c.per_minute_credits} cr</Td>
          <Td className="text-[11px]">{c.notes || (c.is_active ? '' : 'Retired')}</Td>
        </tr>
      ))}
      {!cards.length && <Empty colSpan={5}>No channel rates were ever set.</Empty>}
    </Table>
  </Card>
);

// ---------------------------------------------------------------------------
// What we pay
// ---------------------------------------------------------------------------

/** Per-row "Saved" flash, keyed by the rate's natural key: a save opens a NEW row, so the id changes. */
const useSavedFlash = () => {
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const flash = useCallback((key: string) => {
    setSavedKey(key);
    window.setTimeout(() => setSavedKey((s) => (s === key ? null : s)), 2500);
  }, []);
  return { savedKey, flash };
};

const RateInput: React.FC<{
  symbol: string;
  value: number;
  dirty: boolean;
  saved: boolean;
  disabled: boolean;
  onChange: (v: number) => void;
  onSave: () => void;
  onDiscard: () => void;
}> = ({ symbol, value, dirty, saved, disabled, onChange, onSave, onDiscard }) => (
  <div className="flex items-center justify-end gap-1">
    <span className="text-xs text-gray-400">{symbol}</span>
    <input
      type="number" step="0.0001" min="0" value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onSave();
        if (e.key === 'Escape') onDiscard();
      }}
      disabled={disabled}
      className={`w-24 rounded py-1 text-right text-xs tabular-nums ${
        dirty ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
      }`}
    />
    {dirty ? (
      <button
        onClick={onSave}
        disabled={disabled}
        className="rounded bg-blue-600 px-2 py-1 text-[11px] text-white hover:bg-blue-700 disabled:opacity-50"
        title="Closes the current rate's validity window and starts a new one from now"
      >
        Save
      </button>
    ) : saved ? (
      <span className="text-[11px] font-medium text-emerald-600">Saved</span>
    ) : null}
  </div>
);

const Carriers: React.FC<{
  rows: CarrierRate[];
  api: AdminAPI;
  saving: boolean;
  setSaving: (b: boolean) => void;
  onChanged: () => void;
  onError: (e: string | null) => void;
}> = ({ rows, api, saving, setSaving, onChanged, onError }) => {
  const [edit, setEdit] = useState<Record<number, number>>({});
  const { savedKey, flash } = useSavedFlash();
  const keyOf = (r: CarrierRate) => `${r.provider}:${r.direction}:${r.country_code}`;
  const discard = (id: number) => setEdit((s) => { const n = { ...s }; delete n[id]; return n; });

  const save = async (r: CarrierRate) => {
    const next = edit[r.id];
    if (next === undefined || next === r.per_minute) return;
    setSaving(true);
    onError(null);
    try {
      await api.upsertCarrierRate({
        provider: r.provider, direction: r.direction, country_code: r.country_code,
        per_minute: next, per_call: r.per_call,
        billing_increment_seconds: r.billing_increment_seconds,
        minimum_seconds: r.minimum_seconds, currency: r.currency, notes: r.notes,
      });
      discard(r.id);
      flash(keyOf(r));
      onChanged();
    } catch (e) {
      onError(`Couldn't save the ${capitalise(r.provider)} ${r.direction} rate: ${errorText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const estimated = rows.filter((r) => (r.notes || '').includes('ESTIMATE')).length;

  return (
    <Card
      title="Carrier minutes"
      subtitle="What the PSTN leg costs us. Calls are priced at hangup against the rate live at that moment."
      bodyClassName=""
    >
      {estimated > 0 && (
        <div className="px-4 pt-3">
          <Notice tone="warn">
            {estimated} of these are seeded <strong>estimates</strong>, not contracted rates. Every
            phone cost figure on this dashboard is only as good as they are.
          </Notice>
        </div>
      )}
      <Table
        head={
          <tr>
            <Th>Provider</Th>
            <Th>Direction</Th>
            <Th>Destination</Th>
            <Th align="right">Per minute</Th>
            <Th align="right" title="Carriers bill in whole increments, not seconds">Increment</Th>
            <Th align="right">Minimum</Th>
            <Th>Notes</Th>
          </tr>
        }
      >
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-gray-50">
            <Td className="font-medium text-gray-900">{capitalise(r.provider)}</Td>
            <Td>{capitalise(r.direction)}</Td>
            <Td>{r.country_code === '*' ? <span className="text-gray-400">Any</span> : r.country_code}</Td>
            <Td align="right">
              <RateInput
                symbol={symbolFor(r.currency)}
                value={edit[r.id] ?? r.per_minute}
                dirty={edit[r.id] !== undefined && edit[r.id] !== r.per_minute}
                saved={savedKey === keyOf(r)}
                disabled={saving}
                onChange={(v) => setEdit((s) => ({ ...s, [r.id]: v }))}
                onSave={() => save(r)}
                onDiscard={() => discard(r.id)}
              />
            </Td>
            <Td align="right" className="tabular-nums text-gray-500">{r.billing_increment_seconds}s</Td>
            <Td align="right" className="tabular-nums text-gray-500">
              {r.minimum_seconds ? `${r.minimum_seconds}s` : '—'}
            </Td>
            <Td className="max-w-xs truncate text-[11px] text-gray-500" title={r.notes ?? ''}>{r.notes}</Td>
          </tr>
        ))}
        {!rows.length && <Empty colSpan={7}>No carrier rates. Phone calls are costed at zero until one exists.</Empty>}
      </Table>
      <div className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500">
        Editing a rate closes the old one's validity window rather than overwriting it, so a call
        priced last month can still be explained by the rate that was live when it happened.
      </div>
    </Card>
  );
};

/**
 * What a WhatsApp or SMS message costs us.
 *
 * The gaps table above the rates is the important half. A message with no
 * matching rate row is costed at zero and stamped unpriced — which on a cost
 * dashboard does not look like a bug, it looks like a cheaper month. Showing
 * the exact lookup that missed turns it into a row someone can add.
 */
const MessagingRates: React.FC<{
  rows: MessagingRate[];
  gaps: UnpricedMessaging[];
  api: AdminAPI;
  saving: boolean;
  setSaving: (b: boolean) => void;
  onChanged: () => void;
  onError: (e: string | null) => void;
}> = ({ rows, gaps, api, saving, setSaving, onChanged, onError }) => {
  const [edit, setEdit] = useState<Record<number, number>>({});
  const { savedKey, flash } = useSavedFlash();
  const keyOf = (r: MessagingRate) => `${r.provider}:${r.channel}:${r.category}:${r.direction}:${r.country_code}`;
  const discard = (id: number) => setEdit((s) => { const n = { ...s }; delete n[id]; return n; });

  const save = async (r: MessagingRate) => {
    const next = edit[r.id];
    if (next === undefined || next === r.per_message) return;
    setSaving(true);
    onError(null);
    try {
      await api.upsertMessagingRate({
        provider: r.provider, channel: r.channel, category: r.category,
        direction: r.direction, country_code: r.country_code,
        per_message: next, per_conversation: r.per_conversation,
        segment_chars: r.segment_chars, currency: r.currency, notes: r.notes,
      });
      discard(r.id);
      flash(keyOf(r));
      onChanged();
    } catch (e) {
      onError(`Couldn't save the ${TRANSPORT_LABEL[r.channel] ?? r.channel} rate: ${errorText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const addFromGap = async (g: UnpricedMessaging) => {
    if (!g.provider || !g.channel) return;
    setSaving(true);
    onError(null);
    try {
      await api.upsertMessagingRate({
        provider: g.provider, channel: g.channel,
        category: g.category ?? 'any', direction: 'outbound',
        country_code: g.country_code ?? '*',
        per_message: 0,
        // SMS is invoiced per 160-char segment; WhatsApp and RCS are per message.
        segment_chars: g.channel === 'sms' ? 160 : 0,
        notes: 'Created from an unpriced send — set the real rate',
      });
      onChanged();
    } catch (e) {
      onError(`Couldn't add a rate row: ${errorText(e)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Message rates"
      subtitle="What WhatsApp and SMS cost us. Priced at send against the rate live at that moment."
      bodyClassName=""
    >
      {gaps.length > 0 && (
        <div className="px-4 pt-3">
          <Notice tone="warn">
            <div className="mb-1.5">
              <strong>{gaps.reduce((n, g) => n + g.messages, 0).toLocaleString()} messages</strong> were sent with no
              matching rate and are counted at zero. Every messaging cost figure is short by whatever
              they really cost.
            </div>
            <div className="flex flex-wrap gap-1.5">
              {gaps.slice(0, 6).map((g, i) => (
                <button
                  key={i}
                  onClick={() => addFromGap(g)}
                  disabled={saving}
                  className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[11px] text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                  title="Add a rate row for this exact lookup"
                >
                  {[g.provider, g.channel, g.category, g.country_code].filter(Boolean).join(' · ')}
                  <span className="ml-1 text-amber-500">×{g.messages.toLocaleString()}</span>
                </button>
              ))}
            </div>
          </Notice>
        </div>
      )}
      <Table
        head={
          <tr>
            <Th>Provider</Th>
            <Th>Channel</Th>
            <Th title="WhatsApp prices by template category; marketing costs several times a service reply">Category</Th>
            <Th>Destination</Th>
            <Th align="right">Per message</Th>
            <Th align="right" title="Charged once when a 24-hour WhatsApp conversation opens">Per conversation</Th>
            <Th align="right" title="SMS is invoiced per segment; 0 means charged per message however long">Segment</Th>
          </tr>
        }
      >
        {rows.map((r) => (
          <tr key={r.id} className="hover:bg-gray-50">
            <Td className="font-medium text-gray-900">{capitalise(r.provider)}</Td>
            <Td>{TRANSPORT_LABEL[r.channel] ?? capitalise(r.channel)}</Td>
            <Td>{r.category === 'any' ? <span className="text-gray-400">Any</span> : capitalise(r.category)}</Td>
            <Td>{r.country_code === '*' ? <span className="text-gray-400">Any</span> : r.country_code}</Td>
            <Td align="right">
              <RateInput
                symbol={symbolFor(r.currency)}
                value={edit[r.id] ?? r.per_message}
                dirty={edit[r.id] !== undefined && edit[r.id] !== r.per_message}
                saved={savedKey === keyOf(r)}
                disabled={saving}
                onChange={(v) => setEdit((s) => ({ ...s, [r.id]: v }))}
                onSave={() => save(r)}
                onDiscard={() => discard(r.id)}
              />
            </Td>
            <Td align="right" className="tabular-nums text-gray-500">
              {r.per_conversation ? rate(r.per_conversation, '', symbolFor(r.currency)) : '—'}
            </Td>
            <Td align="right" className="tabular-nums text-gray-500">
              {r.segment_chars ? `${r.segment_chars} ch` : '—'}
            </Td>
          </tr>
        ))}
        {!rows.length && (
          <Empty colSpan={7}>
            No message rates yet. When a send has no matching rate it appears above as a gap you can turn into a row.
          </Empty>
        )}
      </Table>
      <div className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500">
        No rates ship by default: a wrong price produces confident, incorrect margins, so messages
        stay visibly unpriced until someone enters the real numbers.
      </div>
    </Card>
  );
};

const Models: React.FC<{ rows: UsageRateCard['models'] }> = ({ rows }) => {
  const { unitRate } = useCurrency();
  return (
    <Card title="Model rates" subtitle="Edited on the Model pricing page" bodyClassName="">
      <div className="max-h-96 overflow-y-auto">
        <Table
          head={<tr><Th>Model</Th><Th>Type</Th><Th align="right">Rate</Th></tr>}
        >
          {rows.map((m) => (
            <tr key={m.id}>
              <Td>
                <div className="text-gray-900">{m.model}</div>
                <div className="text-[11px] text-gray-400">{capitalise(m.provider)}</div>
              </Td>
              <Td><Chip kind="modality" value={m.modality} /></Td>
              <Td align="right" className="whitespace-nowrap text-xs tabular-nums">
                {m.per_million_chars != null ? unitRate(m.per_million_chars, '/1M chars')
                  : m.per_audio_minute != null ? unitRate(m.per_audio_minute, '/audio min')
                  : `${unitRate(m.input_per_mtok, '')} / ${unitRate(m.output_per_mtok, '')} per 1M tokens`}
              </Td>
            </tr>
          ))}
          {!rows.length && <Empty colSpan={3}>No model rates. Calls are recorded at zero until one exists.</Empty>}
        </Table>
      </div>
    </Card>
  );
};

const CreditPrices: React.FC<{
  actions: UsageRateCard['credit_actions'];
  settings: UsageRateCard['pricing_settings'];
}> = ({ actions, settings }) => (
  <Card
    title="Credit prices"
    subtitle="The default credits per thing, edited on the Rate card page. The credit value below is internal, for margins only."
    bodyClassName=""
  >
    <Table head={<tr><Th>Action</Th><Th align="right">Credits</Th></tr>}>
      {actions.map((a) => (
        <tr key={a.action}>
          <Td className="text-gray-700">{ACTION_LABEL[a.action] ?? capitalise(a.action.replace(/_/g, ' '))}</Td>
          <Td align="right" className="tabular-nums">{a.credits.toLocaleString()}</Td>
        </tr>
      ))}
      {!actions.length && <Empty colSpan={2}>No per-action credits set. Set them on the Rate card page.</Empty>}
    </Table>
    <div className="border-t border-gray-100 px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-gray-400">Credit value</div>
      <div className="mt-1 flex flex-wrap gap-4">
        {settings.map((s) => (
          <div key={s.currency} className="text-sm tabular-nums text-gray-800">
            <span className="font-medium">{s.currency}</span> {s.base_price_per_credit} per credit
          </div>
        ))}
        {!settings.length && <span className="text-sm text-gray-400">No credit value set — revenue reports as zero.</span>}
      </div>
    </div>
  </Card>
);
